import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import VoiceRecorder from "@/components/VoiceRecorder";
import VoicePlayer from "@/components/VoicePlayer";
import { Send, Circle } from "lucide-react";

export default function AdminChat() {
  const [convos, setConvos] = useState([]);
  const [selected, setSelected] = useState(null);
  const [text, setText] = useState("");

  const load = () => api.get("/chat/conversations").then((r) => setConvos(r.data));
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, []);

  const open = async (c) => {
    const { data } = await api.get(`/chat/conversations/${c.id}`);
    setSelected(data);
    load();
  };

  const send = async () => {
    if (!text.trim() || !selected) return;
    await api.post(`/chat/conversations/${selected.conversation.id}/messages`, { body: text });
    setText("");
    open(selected.conversation);
  };

  // Voice-note reply — mirrors the member side. Uses attachments with kind:"voice".
  const sendVoice = async ({ url, duration_sec, content_type }) => {
    if (!selected) return;
    await api.post(`/chat/conversations/${selected.conversation.id}/messages`, {
      body: "🎙️ رسالة صوتية",
      attachments: [{ url, name: `voice.${(content_type || "").split("/")[1] || "webm"}`,
                      content_type: content_type || "audio/webm",
                      kind: "voice", duration_sec }],
    });
    open(selected.conversation);
  };

  return (
    <div className="space-y-4" data-testid="admin-chat">
      <h1 className="text-3xl font-display font-semibold">رسائل المدرب</h1>
      <div className="grid md:grid-cols-3 gap-4 h-[70vh]">
        <Card className="bg-white border-foreground/10 overflow-y-auto p-2">
          {convos.map((c) => (
            <button
              key={c.id}
              onClick={() => open(c)}
              data-testid={`conv-${c.id}`}
              className={`w-full text-start p-3 rounded-lg text-sm mb-1 ${selected?.conversation?.id === c.id ? "bg-black text-white" : "hover:bg-foreground/5"}`}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium">{c.member?.full_name}</span>
                {c.unread_by_coach > 0 && <Badge>{c.unread_by_coach}</Badge>}
              </div>
              <p className="text-xs opacity-70 mt-1 truncate">{c.last_message_preview || "بدون رسائل"}</p>
            </button>
          ))}
          {convos.length === 0 && <div className="text-center opacity-60 py-8 text-sm">لا محادثات</div>}
        </Card>

        <Card className="md:col-span-2 bg-white border-foreground/10 overflow-hidden flex flex-col">
          {!selected ? (
            <div className="flex-1 grid place-items-center opacity-60">اختر محادثة</div>
          ) : (
            <>
              <div className="p-4 border-b border-foreground/10">
                <p className="font-medium">{selected.conversation.member?.full_name}</p>
                <p className="text-xs opacity-70">{selected.conversation.member?.email}</p>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="admin-chat-messages">
                {selected.messages.map((m) => {
                  const mine = m.from_role === "coach";
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`chat-bubble ${mine ? "bg-black text-white" : "bg-foreground/5"}`}>
                        {m.attachments?.map((a, i) => {
                          const url = a.url?.startsWith("http") ? a.url : `${process.env.REACT_APP_BACKEND_URL}${a.url || ""}`;
                          const isVoice = a.kind === "voice" || (a.content_type || "").startsWith("audio/");
                          const isImg = (a.content_type || "").startsWith("image/");
                          if (isVoice) return <VoicePlayer key={i} url={url} durationSec={a.duration_sec} testid={`admin-voice-${m.id}`} />;
                          return isImg
                            ? <img key={i} src={url} alt="" className="max-h-56 rounded-lg mb-2" />
                            : <a key={i} href={url} target="_blank" rel="noreferrer" className="block underline text-sm mb-1">{a.name || "ملف"}</a>;
                        })}
                        {!(m.attachments?.some((a) => a.kind === "voice")) && (
                          <p className="whitespace-pre-wrap">{m.body}</p>
                        )}
                        <div className="text-[10px] opacity-60 mt-1">
                          {new Date(m.created_at).toLocaleString("ar", { hour: "2-digit", minute: "2-digit" })}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="p-3 border-t border-foreground/10 flex gap-2">
                <VoiceRecorder onSent={sendVoice} />
                <Textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder="اكتب ردك..."
                  className="min-h-[48px] flex-1 bg-foreground/5 border-foreground/10"
                  data-testid="admin-chat-input"
                />
                <Button onClick={send} size="icon" className="h-11 w-11 rounded-full" data-testid="admin-chat-send"><Send className="h-4 w-4 rtl:-scale-x-100" /></Button>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
